import React, { useState, useEffect } from "react";
import axios from "axios";

const API = "https://daily-report.spectratally.net/api";

import "./ReportList.css";

export default function ReportList({ onEdit, onNew }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports`);
      setReports(res.data.data.reverse()); // newest first
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/reports/${id}`);
      fetchReports();
    } catch {
      alert("Failed to delete.");
    } finally {
      setDeleting(null);
    }
  };

  const filtered = reports.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.employeeName || "").toLowerCase().includes(q) ||
      (r.companyName  || "").toLowerCase().includes(q) ||
      (r.department   || "").toLowerCase().includes(q) ||
      (r.date         || "").includes(q)
    );
  });

  // Total amount across customers
  const totalAmount = (r) =>
    (r.customers || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  if (loading) {
    return (
      <div className="list-loader">
        <div className="spinner" />
        <p>Loading reports…</p>
      </div>
    );
  }

  return (
    <div className="list-container">
      {/* Header */}
      <div className="list-header">
        <div>
          <h2 className="list-title">📂 All Reports</h2>
          <p className="list-sub">{reports.length} report{reports.length !== 1 ? "s" : ""} saved</p>
        </div>
        <div className="list-actions">
          <input
            className="search-input"
            placeholder="🔍 Search by name, date, dept…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="btn-new" onClick={onNew}>➕ New Report</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>{search ? "No matching reports found" : "No reports yet"}</h3>
          <p>{search ? "Try a different search term" : "Create your first daily report!"}</p>
          {!search && <button className="btn-new" onClick={onNew}>Create Report</button>}
        </div>
      ) : (
        <div className="cards-grid">
          {filtered.map((r) => (
            <div key={r.id} className="report-card-item">
              {/* Card header */}
              <div className="rci-header">
                <div>
                  <div className="rci-date">📅 {r.date || "—"}</div>
                  <div className="rci-employee">{r.employeeName || "Unknown Employee"}</div>
                </div>
                <div className="rci-dept-badge">{r.department || "—"}</div>
              </div>

              {/* Stats row */}
              <div className="rci-stats">
                <Stat label="Total DSC"  value={r.dsc?.totalDSC    || "—"} />
                <Stat label="Customers"  value={(r.customers || []).filter((c) => c.customerName).length} />
                <Stat label="Amount (₹)" value={totalAmount(r) ? `₹${totalAmount(r).toLocaleString("en-IN")}` : "—"} />
                <Stat label="Payment"    value={r.payment?.mode     || "—"} />
              </div>

              {/* DSC breakdown */}
              {r.dsc && (
                <div className="rci-dsc-row">
                  {[["Signing", r.dsc.signing], ["Combo", r.dsc.combo], ["DGFT", r.dsc.dgft], ["Other", r.dsc.other]]
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <span key={k} className="dsc-chip">{k}: {v}</span>
                    ))}
                </div>
              )}

              {/* Footer */}
              <div className="rci-footer">
                <span className="rci-company">{r.companyName || "—"}</span>
                <div className="rci-btns">
                  <button className="rci-btn edit" onClick={() => onEdit(r)}>✏️ Edit</button>
                  <button
                    className="rci-btn delete"
                    onClick={() => handleDelete(r.id)}
                    disabled={deleting === r.id}
                  >
                    {deleting === r.id ? "⏳" : "🗑️"} Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat-box">
      <div className="stat-val">{value}</div>
      <div className="stat-lbl">{label}</div>
    </div>
  );
}
